/* ==========================================================================
   Desktop Sticky Cart — analytics parity module.

   Fires the SAME funnel events the existing cart/checkout flow fires, at the
   equivalent drawer step transitions, so control (full /cart page) and variant
   (drawer) produce a comparable funnel. The existing global emitters
   (sendViewCartEvent / fireBeginCheckoutEvent) live in cart-page-only snippets,
   so we re-emit here using the GLOBAL context builders that exist everywhere:
   window.sessionCore.getMergedContext(), window.getLocationData(), and the
   gtag / dataLayer / analytics (Segment) / clevertap SDKs.

   Item enrichment (categories from metafields, etc.) is server-rendered into the
   [data-st-analytics] JSON by the section. Exposes window.StickyCartAnalytics.
   ========================================================================== */
(function () {
  'use strict';

  function ctx() {
    try {
      return window.sessionCore && window.sessionCore.getMergedContext
        ? window.sessionCore.getMergedContext()
        : {};
    } catch (e) {
      return {};
    }
  }

  function loc() {
    try {
      return window.getLocationData ? window.getLocationData() : null;
    } catch (e) {
      return null;
    }
  }

  function cartId() {
    try {
      return (localStorage.getItem('cartId') || '').replace('gid://shopify/Cart/', '') || '';
    } catch (e) {
      return '';
    }
  }

  // cart.token's "?key=<key>" half, split off and stored separately at
  // write time (layout/theme.liquid) alongside the clean cartId.
  function cartKey() {
    try {
      return localStorage.getItem('cartKey') || '';
    } catch (e) {
      return '';
    }
  }

  function discountCode() {
    try {
      return localStorage.getItem('discountCode') || null;
    } catch (e) {
      return null;
    }
  }

  // Memoized JSON.parse keyed by the raw source string. The same large blobs (cart analytics data,
  // EDD caches, user location) get read once PER ITEM across several Segment product-array builds
  // on a single Proceed click; re-parsing them every time froze the click→Review transition on
  // large carts (no network — pure CPU). We re-parse only when the source string actually changes,
  // so 51 reads of an unchanged blob cost one parse instead of 51. Returned objects are treated as
  // read-only by all callers (they only map/read), so sharing the cached reference is safe.
  var _parseCache = {};
  function parseCached(key, raw) {
    var c = _parseCache[key];
    if (c && c.raw === raw) return c.val;
    var val = null;
    try { val = raw ? JSON.parse(raw) : null; } catch (e) { val = null; }
    _parseCache[key] = { raw: raw, val: val };
    return val;
  }

  function readData(scope) {
    var el = (scope || document).querySelector('[data-st-analytics]');
    if (!el) return null;
    return parseCached('analytics', el.textContent);
  }

  function eddMap() {
    return parseCached('cartEdd', sessionStorage.getItem('cartEdd')) || {};
  }

  // Delivery-time text (the "eddText" equivalent), keyed by sku. Preferred over
  // the cartEdd type code so the Segment `edd` field matches the other pages.
  function eddTimeMap() {
    return parseCached('cartDeliveryTime', sessionStorage.getItem('cartDeliveryTime')) || {};
  }

  // GA4 / GTM item shape.
  function gtmItems(data) {
    var edd = eddMap();
    return (data.items || []).map(function (i) {
      return {
        item_id: String(i.product_id || ''),
        item_brand: i.vendor || '',
        coupon: '',
        item_name: i.title || '',
        item_category: i.cat0 || '',
        item_category2: i.cat1 || '',
        item_category3: i.cat2 || '',
        item_category4: i.cat3 || '',
        item_category5: i.cat4 || '',
        item_variant: i.variant_title || String(i.variant_id || ''),
        quantity: i.quantity,
        price: i.price,
        index: i.position,
        discount: i.discount,
        edd: eddInfo(i.sku).edd || edd[i.sku] || ''
      };
    });
  }

  // Segment product shape.
  function segProducts(data) {
    var code = discountCode();
    var edd = eddMap();
    var eddTime = eddTimeMap();
    return (data.items || []).map(function (i) {
      var e = eddInfo(i.sku);
      return {
        brand: i.vendor || null,
        category: { l0: i.cat0 || null, l1: i.cat1 || null, l2: i.cat2 || null, l3: i.cat3 || null, l4: i.cat4 || null },
        coupon_name: code,
        edd: e.edd || eddTime[i.sku] || edd[i.sku] || null,
        position: i.position,
        product_coupon_discount: i.discount || 0,
        product_id: i.product_id || null,
        product_name: i.title || null,
        product_url: i.handle || null,
        product_value_before_cart_discount: i.mrp,
        product_value_mrp: i.mrp,
        product_value_post_cart_discount: i.price,
        quantity: i.quantity,
        sku_id: i.sku || null,
        sku_mrp: i.mrp,
        sku_selling_price: i.price,
        variant_id: i.variant_id || null,
        variant_name: i.variant_title || null,
        variant_url: i.handle || null,
        edd_response: e.response || window.emptyEddResponse()
      };
    });
  }

  // itemInSessionStorage shape (mirrors the legacy /cart writer cart-revamp.liquid:4842) — consumed
  // by page.checking-payment.liquid to build GA `purchase` + CleverTap `Charged`, and by
  // sticky-cart-checkout's buildAttributionData. Reuses the SAME [data-st-analytics] source + edd /
  // coupon lookups as gtmItems/segProducts, so there's ONE parser and ONE set of lookups here rather
  // than a duplicate copy in the checkout module. eddInfo() supplies the real sticky-cart EDD text.
  function checkoutStorageItems(data) {
    var code = discountCode();
    var edd = eddMap();
    var eddTime = eddTimeMap();
    return (data.items || []).map(function (i, idx) {
      var e = eddInfo(i.sku);
      return {
        item_id: String(i.product_id == null ? '' : i.product_id),
        item_sku: i.sku || '',
        item_brand: i.vendor || '',
        coupon: code,
        item_name: i.title || '',
        item_variant: i.variant_title || String(i.variant_id || ''),
        item_category: i.cat0 || '',
        item_category2: i.cat1 || '',
        item_category3: i.cat2 || '',
        item_category4: i.cat3 || '',
        item_category5: i.cat4 || '',
        quantity: i.quantity,
        price: i.price,
        index: i.position != null ? i.position : (idx + 1),
        discount: i.discount,
        edd: edd[i.sku] || '',
        eddText: e.edd || eddTime[i.sku] || '',
        // Full edd_response snapshot so the sticky attribution / Payment Completed builders
        // don't depend on cartEddInfo (never written outside the /cart page).
        edd_response: e.response || window.emptyEddResponse(),
        mrp: i.mrp,
        url: i.handle || ''
      };
    });
  }

  // Persist the ordered items to sessionStorage['itemInSessionStorage'] right before the sticky cart
  // hands off to payment. The prepaid flow completes off-site (page.checking-payment.liquid), which
  // reads this key to fire GA `purchase` / CleverTap `Charged`; the legacy /cart writer never runs in
  // the sticky flow. Returns the items (or null if the analytics blob isn't present).
  function persistCheckoutItems(scope) {
    try {
      var data = readData(scope);
      if (!data) return null;
      var items = checkoutStorageItems(data);
      sessionStorage.setItem('itemInSessionStorage', JSON.stringify(items));
      return items;
    } catch (e) {
      return null; // never throw into the checkout/order flow
    }
  }

  function cartValue(data) {
    return {
      coupon_discount: data.total_discount || 0,
      fees: 0,
      mrp: data.subtotal || 0,
      post_coupon_value: data.subtotal || 0,
      pre_coupon_value: (data.subtotal || 0) + (data.total_discount || 0),
      product_discount: 0,
      total_discount: data.total_discount || 0
    };
  }

  function track(name, props) {
    try {
      if (window.analytics && typeof window.analytics.track === 'function') {
        window.analytics.track(name, props);
      }
    } catch (e) {}
  }

  function withCtx(props) {
    var l = loc();
    var merged = {};
    var c = ctx();
    for (var k in props) if (Object.prototype.hasOwnProperty.call(props, k)) merged[k] = props[k];
    if (l) merged.user_location = l;
    for (var k2 in c) if (Object.prototype.hasOwnProperty.call(c, k2)) merged[k2] = c[k2];
    return merged;
  }

  /* ====================================================================== */
  /* Sticky-cart Segment hub: every "Sticky Cart …" event flows through       */
  /* emit(), which stamps the 5 global props. ctx() (getMergedContext) already */
  /* carries session_id / session_index; withCtx also attaches user_location. */
  /* ====================================================================== */
  function customerId() {
    try {
      var el = document.querySelector('[data-st-offers-customer]');
      if (el) { var j = JSON.parse(el.textContent); return (j && j.customer && j.customer.id) || null; }
    } catch (e) {}
    return null;
  }

  function globalProps() {
    return {
      platform: 'web',
      source: 'sticky_cart_desktop',
      user_id: customerId(),
      cart_id: cartId() || null,
      cart_key: cartKey() || null
    };
  }

  function emit(name, props) {
    track(name, Object.assign(globalProps(), withCtx(props || {})));
  }

  function contentRoot() { return document.querySelector('[data-st-content-root]'); }
  function rootNum(attr) {
    var r = contentRoot();
    var v = r ? parseInt(r.getAttribute(attr), 10) : NaN;
    return isNaN(v) ? 0 : v;
  }
  function itemCount() { return rootNum('data-item-count'); }
  function uniqueCount() { var d = readData() || {}; return (d.items || []).length; }

  function pincodeOf() {
    var l = parseCached('userLocation', localStorage.getItem('userLocation'));
    return (l && l.pincode) || '';
  }
  // Per-sku EDD + delivery group from the EDD module's cache (sticky-cart-edd.js).
  function eddInfo(sku) {
    var c = parseCached('stCartEddCache', sessionStorage.getItem('stCartEddCache')) || {};
    var r = c[pincodeOf() + '|' + sku];
    if (r) return { edd: r.time || null, group: r.type || null, response: r.er || null };
    return { edd: null, group: null, response: null };
  }

  // One Segment product (spec shape) from a section [data-st-analytics] item.
  function productOne(i) {
    i = i || {};
    var e = eddInfo(i.sku);
    return {
      product_id: i.product_id || null,
      variant_id: i.variant_id || null,
      sku_id: i.sku || null,
      product_name: i.title || null,
      brand: i.vendor || null,
      sku_selling_price: i.price,
      sku_mrp: i.mrp,
      quantity: i.quantity,
      variant_name: i.variant_title || null,
      variant_url: i.handle ? '/products/' + i.handle : null,
      product_url: i.handle ? '/products/' + i.handle : null,
      category: { l0: i.cat0 || null, l1: i.cat1 || null, l2: i.cat2 || null, l3: i.cat3 || null, l4: i.cat4 || null },
      position: i.position,
      delivery_group: e.group,
      edd: e.edd,
      edd_response: e.response || window.emptyEddResponse()
    };
  }
  function products() { var d = readData() || {}; return (d.items || []).map(productOne); }
  // Build one product payload from a rendered line-item element (qty/remove events).
  function productFromEl(el) {
    if (!el) return {};
    var sku = el.getAttribute('data-skuid');
    var d = readData() || {};
    var m = (d.items || []).filter(function (i) { return String(i.sku) === String(sku); })[0];
    return productOne(m || {
      product_id: el.getAttribute('data-product-id'),
      variant_id: el.getAttribute('data-variant-id'),
      sku: sku,
      quantity: parseInt(el.getAttribute('data-qty'), 10) || 0
    });
  }

  // Live cart value assembled from the rendered bill + coupon + fees (₹).
  function liveCartValue() {
    var mrp = rootNum('data-mrp') / 100;
    var subtotal = rootNum('data-subtotal') / 100;
    var coupon = 0;
    try { var d = localStorage.getItem('disc_price'); if (d) coupon = parseInt(d, 10) || 0; } catch (e) {}
    var fees = 0;
    try {
      var f = JSON.parse(sessionStorage.getItem('allFeesData')) || {};
      Object.keys(f).forEach(function (k) { var fe = f[k] && f[k].fee; if (typeof fe === 'number') fees += fe; });
    } catch (e) {}
    var productDiscount = mrp - subtotal; if (productDiscount < 0) productDiscount = 0;
    return {
      mrp: mrp,
      pre_coupon_value: subtotal,
      post_coupon_value: Math.max(0, subtotal - coupon),
      coupon_discount: coupon,
      product_discount: productDiscount,
      fees: fees,
      total_discount: productDiscount + coupon
    };
  }

  function cartFlags() {
    return {
      has_rx_item: rootNum('data-rx-count') > 0,
      has_oos_item: rootNum('data-oos-count') > 0,
      has_free_gift: !!document.querySelector('[data-st-items] [data-free-gift]'),
      coupon_applied: discountCode() || null
    };
  }

  /* ---- Impression helpers (fire-once-per-open / in-viewport) ----------- */
  var seen = {};
  function resetImpressions() { seen = {}; }
  function fireOnce(key, fn) { if (seen[key]) return; seen[key] = 1; try { fn(); } catch (e) {} }
  // Fire cb the first time `el` scrolls into view. cb is a wrapped emitter that
  // self-dedupes per open via fireOnce, so re-binding on each render is safe.
  function observeImpression(el, cb) {
    if (!el || typeof cb !== 'function') return;
    if (!('IntersectionObserver' in window)) { cb(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { io.disconnect(); cb(); } });
    }, { threshold: 0.5 });
    io.observe(el);
  }

  var STEP_NUM = { cart: 1, address: 2, review: 3, payment: 4 };

  /* ====================================================================== */
  /* Sticky Cart spec events (60-event tracking plan)                         */
  /* ====================================================================== */
  /* §1 Lifecycle */
  function opened(trigger) {
    resetImpressions();
    emit('Sticky Cart Opened', Object.assign({
      trigger: trigger || 'add_to_cart',
      cart_quantity: itemCount(),
      cart_sku_unique_count: uniqueCount(),
      cart_value: liveCartValue(),
      coupon_applied: discountCode() || null,
      products: products()
    }, cartFlags()));
  }
  function closed(fromStep, msInDrawer) {
    emit('Sticky Cart Closed', {
      closed_from_step: fromStep || 'cart',
      cart_quantity: itemCount(),
      cart_value: liveCartValue(),
      time_in_drawer_ms: msInDrawer || 0
    });
  }
  function stepViewed(stepName, direction) {
    emit('Sticky Cart Step Viewed', Object.assign({
      step: STEP_NUM[stepName] || 1,
      step_name: stepName,
      navigation_direction: direction || 'forward',
      cart_quantity: itemCount(),
      cart_value: liveCartValue(),
      coupon_applied: discountCode() || null
    }, cartFlags()));
  }

  /* §2 Line items */
  function qtyChanged(increased, el, prevQty, newQty) {
    emit(increased ? 'Sticky Cart Item Quantity Increased' : 'Sticky Cart Item Quantity Decreased',
      Object.assign(productFromEl(el), {
        previous_quantity: prevQty,
        new_quantity: newQty,
        cart_quantity: itemCount(),
        cart_value: liveCartValue()
      }));
  }
  function removeInitiated(el) {
    var p = productFromEl(el);
    emit('Sticky Cart Item Remove Initiated', Object.assign(p, {
      quantity: 1,
      cart_quantity: itemCount(),
      is_rx_item: el && el.getAttribute('data-rx') === 'true',
      is_free_gift: el && el.getAttribute('data-free-gift') === 'true'
    }));
  }
  function itemRemoved(snapshot, qtyRemoved) {
    // snapshot = product payload captured BEFORE the line left the DOM.
    emit('Sticky Cart Item Removed', Object.assign({}, snapshot, {
      quantity: qtyRemoved,
      cart_quantity: itemCount(),
      cart_value: liveCartValue()
    }));
  }
  function deliveryGroupViewed(label, eddDisplay, count, index, isExpress) {
    fireOnce('dgroup:' + index + ':' + label, function () {
      emit('Sticky Cart Delivery Group Viewed', {
        delivery_group_label: label,
        edd_display: eddDisplay,
        item_count_in_group: count,
        group_index: index,
        is_express: !!isExpress
      });
    });
  }

  /* §3 Gift unlock */
  function giftCarouselViewed(total, unlocked, nextAmt) {
    fireOnce('giftCarousel', function () {
      emit('Sticky Cart Gift Carousel Viewed', {
        total_tiers: total, unlocked_tiers: unlocked,
        next_unlock_amount_needed: nextAmt != null ? nextAmt : null,
        cart_value: liveCartValue()
      });
    });
  }
  function giftTierViewed(p) {
    fireOnce('giftTier:' + (p.gift_variant_id || p.tier_index), function () {
      emit('Sticky Cart Gift Tier Viewed', p);
    });
  }
  function giftClaimClicked(p) { emit('Sticky Cart Gift Claim Clicked', Object.assign({ cart_value: liveCartValue() }, p)); }
  function giftClaimed(p) { emit('Sticky Cart Gift Claimed', Object.assign({ cart_quantity: itemCount(), cart_value: liveCartValue() }, p)); }
  function giftCarouselScrolled(total, unlocked, dir) {
    fireOnce('giftScroll', function () {
      emit('Sticky Cart Gift Carousel Scrolled', { total_tiers: total, unlocked_tiers: unlocked, scroll_direction: dir });
    });
  }

  /* §4 Offer strips */
  function offerStripViewed(text, index, type) {
    fireOnce('offer:' + index, function () {
      emit('Sticky Cart Offer Strip Viewed', { offer_text: text, offer_index: index, offer_type: type });
    });
  }
  function offerStripClicked(text, index, type) {
    emit('Sticky Cart Offer Strip Clicked', { offer_text: text, offer_index: index, offer_type: type });
  }

  /* §5 + §6 Coupon */
  // Numeric coupon savings — prefer the API's discountValue, else parse the first ₹ amount out of
  // the human "Save ₹X" text. Keeps coupon_savings_value populated even when discountValue is null.
  function parseAmt(text) {
    var m = String(text == null ? '' : text).match(/(\d[\d,]*)/);
    return m ? parseInt(m[1].replace(/,/g, ''), 10) : null;
  }
  function couponSaveVal(value, text) {
    return value != null ? value : parseAmt(text);
  }
  function bestCouponDisplayed(code, savingsText, savingsValue) {
    fireOnce('bestCoupon:' + code, function () {
      emit('Sticky Cart Best Coupon Displayed', {
        coupon_code: code, coupon_savings_text: savingsText, coupon_savings_value: couponSaveVal(savingsValue, savingsText),
        cart_value: liveCartValue()
      });
    });
  }
  function couponApplied2(code, discount, from) {
    emit('Sticky Cart Coupon Applied', { coupon_code: code, coupon_discount: discount, coupon_applied_from: from, cart_value: liveCartValue() });
  }
  function couponDenied(code, reason, from) {
    emit('Sticky Cart Coupon Denied', { coupon_code: code, reason: reason, coupon_applied_from: from, cart_value: liveCartValue() });
  }
  function couponRemoved2(code, discountLost) {
    emit('Sticky Cart Coupon Removed', { coupon_code: code, coupon_discount_lost: discountLost || 0, cart_value: liveCartValue() });
  }
  function couponListOpened(coupons) {
    var list = coupons || [];
    var enabled = list.filter(function (c) { return !c.disabled; });
    emit('Sticky Cart Coupon List Opened', {
      available_coupons_count: list.length,
      enabled_coupons_count: enabled.length,
      disabled_coupons_count: list.length - enabled.length,
      coupons: list.map(function (c, i) {
        return { coupon_code: c.couponCode, coupon_savings_text: c.saveText, coupon_savings_value: couponSaveVal(c.discountValue, c.saveText), applicability: c.disabled ? 'disabled' : 'enabled', coupon_index: i };
      }),
      cart_value: liveCartValue()
    });
  }
  function couponListClosed(appliedInSession, ms) {
    emit('Sticky Cart Coupon List Closed', { coupon_applied_in_session: !!appliedInSession, time_on_coupon_list_ms: ms || 0 });
  }
  function couponCodeEntered(code) { emit('Sticky Cart Coupon Code Entered', { coupon_code: code, entry_method: 'manual' }); }
  function couponSelectedFromList(code, value, applicability, index) {
    emit('Sticky Cart Coupon Selected From List', { coupon_code: code, coupon_savings_value: value, applicability: applicability, coupon_index: index });
  }
  function couponReadMore(code, index) { emit('Sticky Cart Coupon Read More Clicked', { coupon_code: code, coupon_index: index }); }

  /* §7 Bill */
  function billDetailsViewed() {
    fireOnce('billDetails', function () {
      var cv = liveCartValue();
      var fees = {};
      try { fees = JSON.parse(sessionStorage.getItem('allFeesData')) || {}; } catch (e) {}
      var del = fees.delivery_fee || {};
      var plat = fees.platform_fee || {};
      var delFee = typeof del.fee === 'number' ? del.fee : 0;   // 'FREE'/absent → 0
      var platFee = typeof plat.fee === 'number' ? plat.fee : 0;
      var platMrp = parseInt(plat.max_fee, 10);
      emit('Sticky Cart Bill Details Viewed', {
        total_mrp: cv.mrp, discount_on_mrp: cv.product_discount, coupon_discount: cv.coupon_discount,
        delivery_fee: delFee,
        delivery_fee_waived: !delFee,
        platform_fee: platFee,
        platform_fee_mrp: isNaN(platMrp) ? platFee : platMrp,
        total_amount: cv.post_coupon_value + cv.fees, total_savings: cv.total_discount,
        items_count: itemCount()
      });
    });
  }
  function billExpanded() {
    var cv = liveCartValue();
    emit('Sticky Cart Bill Expanded', { total_amount: cv.post_coupon_value + cv.fees, coupon_applied: discountCode() || null });
  }

  /* §8 OOS */
  function oosBannerDisplayed(count, oosProducts) {
    fireOnce('oosBanner', function () {
      emit('Sticky Cart OOS Banner Displayed', {
        oos_item_count: count, oos_products: oosProducts || [],
        cart_quantity: itemCount(), cart_value: liveCartValue()
      });
    });
  }
  function oosItemRemoved(p, remaining) {
    emit('Sticky Cart OOS Item Removed', Object.assign({}, p, {
      remaining_oos_count: remaining, cart_quantity: itemCount(), cart_value: liveCartValue()
    }));
  }

  /* §9 Pharmacy / Rx */
  function rxBannerDisplayed(count, rxProducts, hours, vetCopy) {
    fireOnce('rxBanner', function () {
      emit('Sticky Cart Rx Banner Displayed', { rx_item_count: count, rx_products: rxProducts || [], business_hours_state: hours, vet_call_copy: vetCopy });
    });
  }
  function rxUploadInitiated(count, hours) { emit('Sticky Cart Rx Upload Initiated', { rx_item_count: count, business_hours_state: hours }); }
  function rxUploadModalViewed(count, hours) { emit('Sticky Cart Rx Upload Modal Viewed', { rx_item_count: count, business_hours_state: hours }); }
  function rxValidExpanded(count) { emit('Sticky Cart Rx Valid Prescription Expanded', { rx_item_count: count }); }
  function rxUploaded(fmt, count, total) { emit('Sticky Cart Rx Prescription Uploaded', { file_format: fmt, rx_item_count: count, prescriptions_total: total }); }
  function rxUploadFailed(reason, fmt, count) { emit('Sticky Cart Rx Upload Failed', { failure_reason: reason, file_format_attempted: fmt, rx_item_count: count }); }
  function rxUploadedStateViewed(uploaded, count) {
    fireOnce('rxUploadedState', function () { emit('Sticky Cart Rx Uploaded State Viewed', { prescriptions_uploaded: uploaded, rx_item_count: count }); });
  }
  function rxAddMore(already, count) { emit('Sticky Cart Rx Add More Clicked', { prescriptions_already_uploaded: already, rx_item_count: count }); }

  /* §10 Footer */
  function proceedToPay(fromStep, hasAddress, addrPincode) {
    var cv = liveCartValue();
    emit('Sticky Cart Proceed To Pay Clicked', Object.assign({
      from_step: fromStep,
      cart_quantity: itemCount(),
      cart_sku_unique_count: uniqueCount(),
      cart_value: cv,
      coupon_applied: discountCode() || null,
      has_address_set: !!hasAddress,
      address_pincode: addrPincode || null,
      products: products()
    }, cartFlags()));
  }
  function footerAddressChange(fromStep, hasAddress, pincode) {
    emit('Sticky Cart Footer Address Change Clicked', { current_address_set: !!hasAddress, current_pincode: pincode || null, from_step: fromStep });
  }

  /* §11 Address (observable-only) */
  function addressListViewed(savedCount, hasCurrent, pincode, entry) {
    emit('Sticky Cart Address List Viewed', { saved_address_count: savedCount != null ? savedCount : null, has_current_location: !!hasCurrent, current_pincode: pincode || null, entry_point: entry });
  }
  function savedAddressSelected(a) { emit('Sticky Cart Saved Address Selected', a || {}); }
  function useCurrentLocation(state, entry) { emit('Sticky Cart Use Current Location Clicked', { permission_state: state, entry_point: entry }); }
  function permissionGranted(lat, lng, pincode) { emit('Sticky Cart Location Permission Granted', { latitude: lat, longitude: lng, pincode: pincode || null }); }

  /* §12 Map */
  function mapConfirmed(d) { emit('Sticky Cart Map Location Confirmed', d || {}); }
  /* §13 Address form */
  function addressSaved(d) { emit('Sticky Cart Address Saved', d || {}); }

  /* §14 Review */
  function reviewStepViewed(shipmentCount, addrPincode) {
    emit('Sticky Cart Review Step Viewed', Object.assign({
      shipment_count: shipmentCount,
      cart_quantity: itemCount(),
      cart_sku_unique_count: uniqueCount(),
      cart_value: liveCartValue(),
      coupon_applied: discountCode() || null,
      address_pincode: addrPincode || null,
      products: products()
    }, cartFlags()));
  }
  function deliveryEstimateViewed(idx, total, eddDisplay, isExpress, hasRx, vetExpected) {
    fireOnce('estimate:' + idx, function () {
      emit('Sticky Cart Delivery Estimate Viewed', {
        shipment_index: idx, shipment_total: total, edd_display: eddDisplay,
        is_express: !!isExpress, has_rx_in_shipment: !!hasRx, vet_call_expected: !!vetExpected
      });
    });
  }

  /* §15 Payment */
  function paymentStepViewed(methodsList, hasBankOffer, addrPincode) {
    emit('Sticky Cart Payment Step Viewed', {
      cart_quantity: itemCount(),
      cart_value: liveCartValue(),
      coupon_applied: discountCode() || null,
      available_payment_methods: methodsList || [],
      has_bank_offer: !!hasBankOffer,
      address_pincode: addrPincode || null
    });
  }
  function paymentMethodSelected(method, hasCodFee, codFee, hasBankOffer) {
    emit('Sticky Cart Payment Method Selected', {
      payment_method: method,
      has_cod_fee: !!hasCodFee,
      cod_fee_amount: codFee != null ? codFee : null,
      cart_value: liveCartValue(),
      has_bank_offer: !!hasBankOffer
    });
  }
  function codFeeWarning(amount) {
    fireOnce('codFee', function () {
      emit('Sticky Cart COD Fee Warning Displayed', { cod_fee_amount: amount, cart_value: liveCartValue() });
    });
  }
  function orderPlaced(o) {
    o = o || {};
    // o: { transaction_id, payment_method, total_value, total_discount, coupon_code, coupon_discount }
    emit('Sticky Cart Order Placed', Object.assign({
      cart_quantity: itemCount(),
      cart_sku_unique_count: uniqueCount(),
      products: products()
    }, o));

    // GA + CleverTap parity with the cart page's order-success block (addressSummaryPage.liquid
    // purchase / Charged). Fires for the in-drawer COD order; prepaid completes on the
    // checking-payment page which already fires these globally.
    var data = readData();
    var items = (data && data.items) || [];
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'purchase', {
          transaction_id: o.transaction_id || 'unknown',
          value: o.total_value || 0,
          currency: 'INR',
          payment_method: o.payment_method || '',
          items: gtmItems(data || { items: [] }),
          userId: customerId()
        });
      }
    } catch (e) {}
    try {
      if (typeof clevertap !== 'undefined' && clevertap.event && clevertap.event.push) {
        var ctItems = items.map(function (i) {
          return {
            product_id: String(i.product_id || ''),
            brand: i.vendor || '',
            product_name: i.title || '',
            product_category1: i.cat0 || '',
            product_category2: i.cat1 || '',
            product_category3: i.cat2 || '',
            product_category4: i.cat3 || '',
            product_category5: i.cat4 || '',
            variant_id: String(i.variant_id || ''),
            quantity: parseInt(i.quantity, 10) || 1,
            product_sku: i.sku || '',
            price: parseFloat(i.price) || 0,
            variant_name: i.variant_title || ''
          };
        });
        clevertap.event.push('Charged', {
          transaction_id: o.transaction_id || 'unknown',
          total_value: o.total_value || 0,
          coupon: o.coupon_code || 'null',
          total_discount: data ? (data.total_discount || 0) : ((o.total_discount || 0) / 100),
          Items: ctItems
        });
      }
    } catch (e) {}
  }

  /* ---- Events ---------------------------------------------------------- */
  function viewCart(scope, step) {
    var data = readData(scope);
    if (!data) return;
    var id = cartId().split('?')[0];
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_cart',
      cart_id: id,
      cart_key: cartKey(),
      ecommerce: { currency: 'INR', value: data.subtotal, items: gtmItems(data) },
      step_name: step || 'Cart'
    });
    // CleverTap parity with the cart page's "Cart Viewed" (snippets/custom-tracking.liquid:469).
    try {
      if (typeof clevertap !== 'undefined' && clevertap.event && clevertap.event.push) {
        clevertap.event.push('Cart Viewed', { value: data.subtotal, item_quantity: data.item_count });
      }
    } catch (e) {}
  }

  function cartReviewed(scope) {
    var data = readData(scope);
    if (!data) return;
    track('Cart Reviewed', withCtx({
      cart_coupon_used: discountCode() || 0,
      cart_id: cartId(),
      cart_key: cartKey(),
      cart_quantity: data.item_count,
      cart_sku_unique_count: (data.items || []).length,
      cart_value: cartValue(data),
      platform: 'web',
      products: segProducts(data)
    }));
  }

  function beginCheckout(scope) {
    var data = readData(scope);
    if (!data) return;
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'begin_checkout', {
          value: data.subtotal,
          currency: 'INR',
          items: gtmItems(data)
        });
      }
    } catch (e) {}
    try {
      if (typeof clevertap !== 'undefined' && clevertap.event && clevertap.event.push) {
        clevertap.event.push('Checkout Started', {
          cart_total: data.subtotal,
          item_quantity: data.item_count,
          coupon_used: discountCode() || 'null'
        });
      }
    } catch (e) {}
    track('Checkout Started', withCtx({
      platform: 'web',
      products: segProducts(data),
      cart_coupon_used: discountCode(),
      cart_id: cartId(),
      cart_key: cartKey(),
      cart_quantity: data.item_count,
      cart_value: cartValue(data)
    }));
  }

  function addPaymentInfo(scope, method) {
    var data = readData(scope);
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'add_payment_info', {
          value: data ? data.subtotal : 0,
          currency: 'INR',
          payment_method: method,
          items: data ? gtmItems(data) : []
        });
      }
    } catch (e) {}
    track('Payment Method Selected', withCtx({
      cart_id: cartId(),
      cart_key: cartKey(),
      payment_method: method,
      platform: 'web'
    }));
  }

  function couponApplied(code, amount, source) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'coupon_applied', coupon_code: code, amount_saved: amount, source: source || 'Sticky Cart' });
    } catch (e) {}
    try {
      if (typeof clevertap !== 'undefined' && clevertap.event && clevertap.event.push) {
        clevertap.event.push('Coupon Applied', { CouponCode: code, AmountSaved: amount, source: source || 'Sticky Cart' });
      }
    } catch (e) {}
    track('Coupon Applied', withCtx({
      cart_id: cartId(),
      cart_key: cartKey(),
      coupon_applied_from: source || 'Sticky Cart',
      coupon_name: code,
      platform: 'web',
      coupon_discount: amount || null
    }));
  }

  function couponRemoved(code) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'coupon_removed', coupon_code: code });
    } catch (e) {}
    track('Coupon Removed', withCtx({ cart_id: cartId(), cart_key: cartKey(), coupon_name: code, platform: 'web' }));
  }

  function couponListViewed(coupons) {
    var enabled = (coupons || []).filter(function (c) { return !c.disabled; });
    var disabled = (coupons || []).filter(function (c) { return c.disabled; });
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'view_coupon_list', enabled_coupon_count: enabled.length, disabled_coupon_count: disabled.length });
    } catch (e) {}
    track('Coupon List Viewed', withCtx({
      cart_id: cartId(),
      cart_key: cartKey(),
      coupons: (coupons || []).map(function (c, i) {
        return { coupon_applicability: c.disabled ? 'Disabled' : 'Enabled', coupon_index: i, coupon_name: c.couponCode, coupon_value: c.discountValue };
      }),
      platform: 'web'
    }));
  }

  window.StickyCartAnalytics = {
    // existing parity events (kept — Checkout Started powers the GrowthBook metric)
    viewCart: viewCart,
    cartReviewed: cartReviewed,
    beginCheckout: beginCheckout,
    addPaymentInfo: addPaymentInfo,
    couponApplied: couponApplied,
    couponRemoved: couponRemoved,
    couponListViewed: couponListViewed,
    // helpers exposed for callers that need to snapshot before a mutation
    productFromEl: productFromEl,
    // checkout hand-off: persist itemInSessionStorage for the off-site purchase/Charged events
    persistCheckoutItems: persistCheckoutItems,
    resetImpressions: resetImpressions,
    observeImpression: observeImpression,
    // §1 lifecycle
    opened: opened,
    closed: closed,
    stepViewed: stepViewed,
    // §2 line items
    qtyChanged: qtyChanged,
    removeInitiated: removeInitiated,
    itemRemoved: itemRemoved,
    deliveryGroupViewed: deliveryGroupViewed,
    // §3 gifts
    giftCarouselViewed: giftCarouselViewed,
    giftTierViewed: giftTierViewed,
    giftClaimClicked: giftClaimClicked,
    giftClaimed: giftClaimed,
    giftCarouselScrolled: giftCarouselScrolled,
    // §4 offers
    offerStripViewed: offerStripViewed,
    offerStripClicked: offerStripClicked,
    // §5/§6 coupon
    bestCouponDisplayed: bestCouponDisplayed,
    couponApplied2: couponApplied2,
    couponDenied: couponDenied,
    couponRemoved2: couponRemoved2,
    couponListOpened: couponListOpened,
    couponListClosed: couponListClosed,
    couponCodeEntered: couponCodeEntered,
    couponSelectedFromList: couponSelectedFromList,
    couponReadMore: couponReadMore,
    // §7 bill
    billDetailsViewed: billDetailsViewed,
    billExpanded: billExpanded,
    // §8 OOS
    oosBannerDisplayed: oosBannerDisplayed,
    oosItemRemoved: oosItemRemoved,
    // §9 pharmacy
    rxBannerDisplayed: rxBannerDisplayed,
    rxUploadInitiated: rxUploadInitiated,
    rxUploadModalViewed: rxUploadModalViewed,
    rxValidExpanded: rxValidExpanded,
    rxUploaded: rxUploaded,
    rxUploadFailed: rxUploadFailed,
    rxUploadedStateViewed: rxUploadedStateViewed,
    rxAddMore: rxAddMore,
    // §10 footer
    proceedToPay: proceedToPay,
    footerAddressChange: footerAddressChange,
    // §11/§12/§13 address
    addressListViewed: addressListViewed,
    savedAddressSelected: savedAddressSelected,
    useCurrentLocation: useCurrentLocation,
    permissionGranted: permissionGranted,
    mapConfirmed: mapConfirmed,
    addressSaved: addressSaved,
    // §14 review
    reviewStepViewed: reviewStepViewed,
    deliveryEstimateViewed: deliveryEstimateViewed,
    // §15 payment
    paymentStepViewed: paymentStepViewed,
    paymentMethodSelected: paymentMethodSelected,
    codFeeWarning: codFeeWarning,
    orderPlaced: orderPlaced
  };
})();
